import React, { useState } from 'react';
import {
  PRICE_FULL_REPORT,
  PRICE_UPGRADE_DELTA,
  COPY_UPGRADE_HEADLINE,
  COPY_CHECKOUT_NOTE,
  MICRO_FULL_REPORT,
  COPY_POST_REPORT_PAYMENT,
  UPGRADE_FRAMING,
  PRICE_KUNDLI_QUESTION,
} from '../constants/pricingCopy';
import { loadRazorpayScript } from '../utils/loadRazorpayScript';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.kundlishastra.com')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5001');

// Must match dashboard tab bar — used in ₹149 copy
export const DASHBOARD_SECTIONS = [
  { name: 'Overview',        desc: 'Lagna, Moon, nakshatra & quick facts' },
  { name: 'Kundli Chart',    desc: 'South Indian chart, house list & planet keys' },
  { name: 'Your Answer',     desc: 'Shastra AI — personalised answer (paid unlock)' },
  { name: 'Planets',         desc: 'Graha table — house, sign, degree, nakshatra' },
  { name: 'Mahadasha',       desc: 'Mahadasha & antardasha timeline' },
  { name: 'Karmic Insights', desc: 'Karmic themes from chart & nakshatra' },
  { name: 'Numerology',      desc: 'Core numbers from name & date of birth' },
];

// ── Full Report only (dashboard insights are free in-app; no ₹149 tier) ───────
const PLANS_ORDERED = [
  {
    type:    'report',
    label:   'Premium PDF — full kundli analysis',
    price:   PRICE_FULL_REPORT,
    color:   '#78350f',
    badge:   '🔥 Best Value',
    highlight: true,
    tagline: 'Mujhe poori kundli PDF chahiye',
    features: [
      '✔ Full kundli analysis premium PDF report',
      '✔ Detailed houses, planets & dasha breakdown',
      '✔ Career, relationship & timing insights',
      '✔ 24 hours me email par delivery',
      `👉 Turant ek sawaal? Dashboard par Shastra AI (${PRICE_KUNDLI_QUESTION})`,
    ],
    warning: null,
  },
];

/** Full Report features when user already paid ₹149 — no second “buy Dashboard” pitch */
const UPGRADE_REPORT_FEATURES = [
  '✔ Dashboard already on file — this payment completes your premium PDF order',
  '✔ Full kundli analysis premium PDF — detailed chart breakdown',
  '✔ Delivered to your registered email within 24 hours',
  '✔ Human-reviewed astrological analysis',
  `👉 Instant one-question answer: Shastra AI (${PRICE_KUNDLI_QUESTION}) on dashboard`,
];

const SPIN_CSS = `
@keyframes ks-spin { to { transform:rotate(360deg); } }
/* Wide modal: use horizontal space to avoid vertical scroll */
.ks-pay-modal-shell {
  width: 100%;
  max-width: min(1040px, 98vw);
  overflow: visible;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08), 0 24px 64px rgba(0, 0, 0, 0.12);
}
.ks-pay-plans-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  align-items: stretch;
}
@media (max-width: 900px) {
  .ks-pay-plans-row { grid-template-columns: 1fr; }
}
/* 7 sections: 4×2 when there’s enough width (shorter); 2×4 on smaller */
.ks-dash-8grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.45rem 0.5rem;
}
@media (min-width: 1100px) {
  .ks-dash-8grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
.ks-pay-upgrade-only {
  grid-template-columns: 1fr;
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
}
`;

/**
 * PaymentModal
 * stage: 'plans' | 'processing' | 'success' | 'error'
 */
export default function PaymentModal({
  isOpen, onClose, onSuccess,
  userId, familyMemberId, memberName,
  userEmail, userName, hasQuestion,
  initialPlan = 'report',
  /** User already had legacy “dashboard” purchase — show upgrade-to-report framing */
  isDashboardUpgrade = false,
}) {
  const { language } = useLanguage();
  const [stage,   setStage]   = useState('plans');
  const [chosen,  setChosen]  = useState(initialPlan);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      if (initialPlan === 'pdf_addon') {
        setStage('pdf_addon');
        setChosen('pdf_addon');
      } else {
        setStage('plans');
        setChosen('report');
      }
      setMessage('');
      setLoading(false);
    }
  }, [isOpen, initialPlan, isDashboardUpgrade]);

  if (!isOpen) return null;

  const initiatePayment = async (planType) => {
    const purchaseType = planType === 'dashboard' ? 'report' : planType;
    setChosen(purchaseType);
    setLoading(true);
    setStage('processing');
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error('Payment gateway failed to load. Please try again.');

      const res = await fetch(`${API_URL}/api/payments/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ purchaseType, userId, familyMemberId, userName: userName || '', userEmail: userEmail || '', language }),
      });
      const order = await res.json();

      if (!res.ok) {
        if (order.alreadyPurchased) {
          setStage('success');
          setMessage(
            purchaseType === 'report'
              ? '✅ Full report already purchased!'
              : purchaseType === 'pdf_addon'
                ? '✅ PDF add-on already purchased!'
                : '✅ Already unlocked!'
          );
          onSuccess?.(order.accessLevel);
          return;
        }
        throw new Error(order.error || 'Failed to create payment order');
      }

      const options = {
        key:         order.keyId,
        amount:      order.amount,
        currency:    order.currency,
        name:        'Kundli Shastra',
        description:
          purchaseType === 'report'
            ? isDashboardUpgrade
              ? `Premium PDF upgrade (${PRICE_UPGRADE_DELTA}) — ${memberName || 'your kundli'}`
              : `Premium kundli PDF report — ${memberName || 'your kundli'}`
            : purchaseType === 'pdf_addon'
              ? `Premium personalised PDF + your question — ${memberName || 'your kundli'}`
              : `Kundli Shastra — ${memberName || 'your kundli'}`,
        order_id:    order.orderId,
        prefill:     { name: userName || '', email: userEmail || '' },
        theme:       { color: '#b45309' },
        handler: async (response) => {
          try {
            const vRes = await fetch(`${API_URL}/api/payments/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                purchaseId:          order.purchaseId,
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
                language,
              }),
            });
            const verified = await vRes.json();
            if (verified.success) {
              setStage('success');
              setMessage(verified.message);
              onSuccess?.(verified.accessLevel);
            } else { throw new Error(verified.error || 'Verification failed'); }
          } catch (err) { setStage('error'); setMessage(err.message); }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            setStage(purchaseType === 'pdf_addon' ? 'pdf_addon' : 'plans');
          },
        },
      };
      new window.Razorpay(options).open();
    } catch (err) {
      setStage('error');
      setMessage(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const closeBtn = (
    <button type="button" onClick={onClose} style={{ background:'rgba(255,255,255,.18)', border:'none', color:'#fff', width:36, height:36, borderRadius:'50%', fontSize:'1.35rem', cursor:'pointer', lineHeight:1, flexShrink:0 }} aria-label="Close">×</button>
  );

  return (
    <div className="ks-modal-scrim" style={{
      position:'fixed', inset:0, zIndex:10000,
      display:'flex', alignItems:'center', justifyContent:'center',
      overflowY:'auto', WebkitOverflowScrolling:'touch',
      padding:'clamp(0.75rem, 3vw, 1.5rem)', paddingTop:'max(1rem, env(safe-area-inset-top, 0px))', paddingBottom:'max(1rem, env(safe-area-inset-bottom, 0px))',
      boxSizing:'border-box',
    }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{SPIN_CSS}</style>
      <div className="ks-pay-modal-shell" style={{ background:'#fff' }}>

        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#78350f,#b45309)', padding:'1.25rem 1.5rem', display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, zIndex:1 }}>
          <div>
            <p style={{ color:'rgba(255,255,255,.85)', fontSize:'0.8rem', margin:0, textTransform:'uppercase', letterSpacing:'0.08em' }}>
              {memberName ? `Kundli for ${memberName}` : 'Your Kundli'}
            </p>
            <h2 style={{ color:'#fef3c7', margin:'0.25rem 0 0', fontSize:'clamp(1.15rem, 2.8vw, 1.35rem)', fontWeight:800, lineHeight:1.25 }}>
              {stage === 'success'    ? '🎉 Payment Successful'   :
               stage === 'error'     ? '❌ Payment Issue'        :
               stage === 'processing'? '⏳ Processing…'          :
               stage === 'pdf_addon' ? '📜 Premium PDF'         :
               isDashboardUpgrade ? '⬆️ Upgrade to Full Report' :
               '🔓 Unlock Your Kundli'}
            </h2>
          </div>
          {stage !== 'processing' && closeBtn}
        </div>

        <div style={{ padding:'1.25rem 1.5rem 1.5rem', fontSize:'16px' }}>

          {/* ── SUCCESS ── */}
          {stage === 'success' && (
            <div style={{ textAlign:'center', padding:'1.5rem 0' }}>
              <div style={{ fontSize:'3rem', marginBottom:'0.5rem' }}>✨</div>
              <p style={{ color:'#15803d', fontWeight:700, fontSize:'1.1rem', margin:'0 0 0.5rem', lineHeight:1.45 }}>{message}</p>
              {(chosen === 'report' || chosen === 'pdf_addon') && (
                <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:'12px', padding:'1rem', marginTop:'0.75rem', fontSize:'0.95rem', color:'#166534', lineHeight:1.5 }}>
                  {chosen === 'report' ? (
                    <>
                      {COPY_POST_REPORT_PAYMENT}. Turant ek sawaal ke liye dashboard par <strong>Shastra AI ({PRICE_KUNDLI_QUESTION})</strong> use karein.
                    </>
                  ) : (
                    <>
                      📄 Your <strong>premium personalised kundli PDF</strong> (with your question included) is being prepared.<br/>
                      <strong>We will email it to your registered address — typically within 24 hours.</strong>
                    </>
                  )}
                </div>
              )}
              <button type="button" onClick={onClose} style={{ marginTop:'1.25rem', background:'linear-gradient(90deg,#78350f,#b45309)', color:'#fff', border:'none', borderRadius:'12px', padding:'0.85rem 2rem', fontWeight:700, cursor:'pointer', fontSize:'1rem' }}>
                View Dashboard →
              </button>
            </div>
          )}

          {/* ── ERROR ── */}
          {stage === 'error' && (
            <div style={{ textAlign:'center', padding:'1rem 0' }}>
              <p style={{ color:'#dc2626', fontWeight:600, fontSize:'1rem', lineHeight:1.5 }}>{message}</p>
              <p style={{ color:'#6b7280', fontSize:'0.9rem', marginTop:'0.5rem' }}>If money was deducted, it will be refunded automatically.</p>
              <button
                type="button"
                onClick={() => setStage(initialPlan === 'pdf_addon' ? 'pdf_addon' : 'plans')}
                style={{ marginTop:'1rem', background:'#f3f4f6', border:'none', borderRadius:'10px', padding:'0.75rem 1.75rem', cursor:'pointer', fontWeight:600, fontSize:'0.95rem' }}
              >
                Try Again
              </button>
            </div>
          )}

          {/* ── PROCESSING ── */}
          {stage === 'processing' && (
            <div style={{ textAlign:'center', padding:'2.5rem 0' }}>
              <div style={{ width:44, height:44, border:'4px solid #fde68a', borderTopColor:'#b45309', borderRadius:'50%', animation:'ks-spin 0.8s linear infinite', margin:'0 auto 1rem' }}></div>
              <p style={{ color:'#92400e', fontWeight:600, fontSize:'1.05rem' }}>Opening payment gateway…</p>
            </div>
          )}

          {/* ── ₹99 PDF add-on (dashboard members with a question) ── */}
          {stage === 'pdf_addon' && (
            <div>
              <div
                style={{
                  borderRadius:'16px',
                  padding:'1.35rem 1.25rem',
                  marginBottom:'1.25rem',
                  background:'linear-gradient(145deg, #fffdfb 0%, #fff7ed 45%, #fef3c7 100%)',
                  border:'1px solid rgba(180, 83, 9, 0.25)',
                  boxShadow:'inset 0 1px 0 rgba(255,255,255,.8)',
                }}
              >
                <p style={{ margin:0, fontSize:'0.68rem', letterSpacing:'0.18em', textTransform:'uppercase', color:'#92400e', fontWeight:700 }}>
                  Premium · Personalised
                </p>
                <h3 style={{ margin:'0.45rem 0 0.5rem', fontFamily:"'Georgia', 'Times New Roman', serif", fontSize:'1.35rem', fontWeight:700, color:'#5c2e0e', lineHeight:1.35 }}>
                  Your kundli &amp; question — crafted as a beautiful PDF
                </h3>
                <p style={{ margin:0, fontSize:'0.95rem', color:'#78350f', lineHeight:1.65 }}>
                  A polished, print-ready report with your chart insights and your exact question woven in — perfect to keep, share, or revisit anytime.
                </p>
                <ul style={{ margin:'0.85rem 0 0', paddingLeft:'1.1rem', color:'#57534e', fontSize:'0.88rem', lineHeight:1.55 }}>
                  <li>Professionally formatted PDF · easy to read on any device</li>
                  <li>Your personal question highlighted in the document</li>
                  <li>Delivered to your registered email</li>
                </ul>
                <div style={{ marginTop:'1rem', display:'flex', alignItems:'baseline', justifyContent:'space-between', gap:'0.75rem', flexWrap:'wrap' }}>
                  <span style={{ fontSize:'1.75rem', fontWeight:900, color:'#78350f' }}>₹99</span>
                  <span style={{ fontSize:'0.8rem', color:'#a16207' }}>One-time · Dashboard members only</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => initiatePayment('pdf_addon')}
                disabled={loading}
                style={{
                  width:'100%',
                  padding:'0.95rem 1rem',
                  background: loading ? '#d1d5db' : 'linear-gradient(90deg,#78350f,#b45309)',
                  color:'#fffbeb',
                  border:'none',
                  borderRadius:'14px',
                  fontWeight:800,
                  fontSize:'1.05rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow:'0 6px 24px rgba(120,53,15,.35)',
                }}
              >
                {loading ? 'Please wait…' : 'Get premium PDF — ₹99'}
              </button>
              <button
                type="button"
                onClick={() => { setStage('plans'); setChosen('report'); }}
                style={{
                  display:'block',
                  width:'100%',
                  marginTop:'0.65rem',
                  padding:'0.65rem',
                  background:'transparent',
                  border:'none',
                  color:'#92400e',
                  fontSize:'0.88rem',
                  fontWeight:600,
                  cursor:'pointer',
                  textDecoration:'underline',
                  textUnderlineOffset:'3px',
                }}
              >
                Full premium PDF analysis? Full Report — {PRICE_FULL_REPORT}
              </button>
              <p style={{ textAlign:'center', fontSize:'0.75rem', color:'#9ca3af', marginTop:'0.85rem', marginBottom:0 }}>
                🔒 Secure payment via Razorpay
              </p>
            </div>
          )}

          {/* ── PLANS ── */}
          {stage === 'plans' && (
            <>
              {isDashboardUpgrade && (
                <div
                  style={{
                    marginBottom: '1rem',
                    padding: '0.9rem 1rem',
                    borderRadius: '14px',
                    background: 'linear-gradient(135deg, rgba(254,243,199,.95), rgba(255,251,235,.98))',
                    border: '1.5px solid rgba(180,83,9,.35)',
                    boxShadow: '0 4px 20px rgba(120,53,15,.12)',
                  }}
                >
                  <p style={{ margin: 0, fontWeight: 800, fontSize: '0.95rem', color: '#5c2e0e', lineHeight: 1.35 }}>
                    {COPY_UPGRADE_HEADLINE}
                  </p>
                  <p style={{ margin: '0.35rem 0 0', fontSize: '0.78rem', color: '#78716c', lineHeight: 1.45 }}>
                    {COPY_CHECKOUT_NOTE}
                  </p>
                </div>
              )}

              {/* Question notice */}
              {hasQuestion && (
                <div style={{ background:'#fffbeb', border:'1.5px solid #fcd34d', borderRadius:'12px', padding:'0.75rem 1rem', marginBottom:'1rem', fontSize:'0.9375rem', color:'#92400e', lineHeight:1.45 }}>
                  {isDashboardUpgrade ? (
                    <>⚠️ Turant jawab ke liye dashboard par <strong>Shastra AI ({PRICE_KUNDLI_QUESTION})</strong>. <strong>Premium PDF</strong> ({PRICE_FULL_REPORT} value) — ab sirf {PRICE_UPGRADE_DELTA} due for full PDF analysis.</>
                  ) : (
                    <>⚠️ Turant ek sawaal: <strong>Shastra AI ({PRICE_KUNDLI_QUESTION})</strong> on dashboard. <strong>Premium PDF</strong> ({PRICE_FULL_REPORT}) = full detailed kundli report by email.</>
                  )}
                </div>
              )}

              {!isDashboardUpgrade && (
                <div
                  style={{
                    marginBottom: '0.9rem',
                    padding: '0.75rem 0.9rem',
                    borderRadius: '12px',
                    background: 'linear-gradient(90deg, #fff7ed, #fef3c7)',
                    border: '1.5px solid #f59e0b',
                    color: '#9a3412',
                    fontWeight: 800,
                    fontSize: '0.9rem',
                    lineHeight: 1.35,
                  }}
                >
                  👉 Premium PDF = full analysis by email · turant Q&amp;A = Shastra AI {PRICE_KUNDLI_QUESTION}
                </div>
              )}

              {isDashboardUpgrade ? (
                <div className="ks-pay-plans-row ks-pay-upgrade-only">
                  <div
                    style={{
                      border: '2.5px solid #78350f',
                      borderRadius: '14px',
                      padding: '0.85rem 0.9rem 1rem',
                      position: 'relative',
                      background: 'linear-gradient(165deg, #fffbeb 0%, #fff7ed 55%, #fef3c7 100%)',
                      boxShadow: '0 10px 36px rgba(120,53,15,.25), 0 0 0 1px rgba(251,191,36,.4)',
                    }}
                  >
                    <span style={{ position:'absolute', top:-10, right:10, background:'linear-gradient(90deg,#78350f,#b45309)', color:'#fef3c7', fontSize:'0.65rem', fontWeight:800, padding:'0.15rem 0.55rem', borderRadius:'999px', letterSpacing:'0.04em', boxShadow:'0 2px 8px rgba(120,53,15,.35)' }}>
                      ⭐ UPGRADE
                    </span>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'0.5rem', marginBottom:'0.25rem', paddingLeft: 20 }}>
                      <span style={{ fontWeight:800, fontSize:'0.95rem', color:'#78350f', lineHeight:1.25 }}>Premium PDF — full kundli analysis</span>
                      <div style={{ textAlign:'right', flexShrink:0 }}>
                        <span style={{ fontWeight:900, fontSize:'1.35rem', color:'#78350f', display:'block' }}>{PRICE_UPGRADE_DELTA}</span>
                        <span style={{ fontSize:'0.72rem', color:'#78716c', fontWeight:600 }}>due now · {PRICE_FULL_REPORT} total value</span>
                      </div>
                    </div>
                    <p style={{ margin:'0 0 0.45rem', fontSize:'0.8rem', color:'#b45309', fontWeight:600, lineHeight:1.35 }}>One payment completes your upgrade — no second Dashboard purchase</p>
                    <ul style={{ margin:0, padding:0, listStyle:'none', display:'flex', flexDirection:'column', gap:'0.22rem' }}>
                      {UPGRADE_REPORT_FEATURES.map((f, i) => (
                        <li key={i} style={{ fontSize:'0.8rem', color:'#374151', lineHeight:1.35 }}>{f}</li>
                      ))}
                    </ul>
                    <p style={{ margin:'0.45rem 0 0', fontSize:'0.75rem', color:'#9a3412', fontWeight:700, background:'#fff7ed', padding:'0.4rem 0.5rem', borderRadius:'8px', border:'1px solid #fed7aa', lineHeight:1.35 }}>⚠️ PDF = detailed report by email within 24h · instant Q&amp;A = Shastra AI ({PRICE_KUNDLI_QUESTION})</p>
                    <div style={{ position:'absolute', top:10, left:10, width:18, height:18, borderRadius:'50%', background:'#78350f', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <span style={{ color:'#fff', fontSize:'0.55rem', fontWeight:900 }}>✓</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="ks-pay-plans-row ks-pay-upgrade-only">
                  {PLANS_ORDERED.map(plan => {
                    const selected = chosen === plan.type;
                    const premium = plan.highlight;
                    return (
                      <div key={plan.type}
                        onClick={() => setChosen(plan.type)}
                        style={{
                          border: premium
                            ? '2.5px solid #78350f'
                            : selected ? `2px solid #b45309` : '1px solid #e7e5e4',
                          borderRadius: '14px',
                          padding: '0.85rem 0.9rem 1rem',
                          cursor: 'pointer',
                          position: 'relative',
                          background: premium ? 'linear-gradient(165deg, #fffbeb 0%, #fff7ed 55%, #fef3c7 100%)' : selected ? '#fff9f0' : '#fafaf9',
                          boxShadow: premium
                            ? '0 14px 40px rgba(120,53,15,.30), 0 0 0 2px rgba(251,191,36,.45)'
                            : '0 1px 2px rgba(0,0,0,.04)',
                          transition: 'all .15s',
                          minHeight: 0,
                          opacity: premium ? 1 : 0.92,
                        }}>
                        {plan.badge && (
                          <span style={{ position:'absolute', top:-10, right:10, background:'linear-gradient(90deg,#78350f,#b45309)', color:'#fef3c7', fontSize:'0.65rem', fontWeight:800, padding:'0.15rem 0.55rem', borderRadius:'999px', letterSpacing:'0.04em', boxShadow:'0 2px 8px rgba(120,53,15,.35)' }}>
                            {plan.badge}
                          </span>
                        )}
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'0.5rem', marginBottom:'0.25rem', paddingLeft: selected ? 20 : 0 }}>
                          <span style={{ fontWeight:800, fontSize:'0.95rem', color: plan.highlight ? '#78350f' : '#1c1917', lineHeight:1.25 }}>{plan.label}</span>
                          <span style={{ fontWeight:900, fontSize:'1.2rem', color:plan.color, flexShrink:0 }}>{plan.price}</span>
                        </div>
                        <p style={{ margin:'0 0 0.45rem', fontSize:'0.8rem', color:'#b45309', fontWeight:600, lineHeight:1.35 }}>{plan.tagline}</p>
                        {plan.type === 'report' ? (
                          <ul style={{ margin:0, padding:0, listStyle:'none', display:'flex', flexDirection:'column', gap:'0.22rem' }}>
                            {plan.features.map((f, i) => (
                              <li key={i} style={{ fontSize:'0.8rem', color:'#374151', lineHeight:1.35 }}>{f}</li>
                            ))}
                          </ul>
                        ) : (
                          <>
                            <ul style={{ margin:0, padding:0, listStyle:'none', display:'flex', flexDirection:'column', gap:'0.22rem' }}>
                              {plan.features.map((f, i) => (
                                <li key={i} style={{ fontSize:'0.8rem', color:'#44403c', lineHeight:1.35, fontWeight:i===0 ? 700 : 500 }}>
                                  {f}
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                        {plan.warning && (
                          <p style={{ margin:'0.45rem 0 0', fontSize:'0.75rem', color:'#9a3412', fontWeight:700, background:'#fff7ed', padding:'0.4rem 0.5rem', borderRadius:'8px', border:'1px solid #fed7aa', lineHeight:1.35 }}>{plan.warning}</p>
                        )}
                        {selected && (
                          <div style={{ position:'absolute', top:10, left:10, width:18, height:18, borderRadius:'50%', background:plan.color, display:'flex', alignItems:'center', justifyContent:'center' }}>
                            <span style={{ color:'#fff', fontSize:'0.55rem', fontWeight:900 }}>✓</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {!isDashboardUpgrade && (
                <ul style={{ margin:'0.85rem 0 0', padding:'0 0 0 1.1rem', fontSize:'0.82rem', color:'#44403c', lineHeight:1.55, listStyle:'none' }}>
                  {MICRO_FULL_REPORT.map((line) => (
                    <li key={line} style={{ marginBottom:'0.25rem', position:'relative', paddingLeft:'0.1rem' }}>
                      <span style={{ color:'#059669', fontWeight:800, marginRight:'0.35rem' }}>✔</span>
                      {line}
                    </li>
                  ))}
                </ul>
              )}

              {/* Primary CTA — Full Report */}
              <button
                type="button"
                onClick={() => initiatePayment('report')}
                disabled={loading}
                style={{ marginTop:'0.85rem', width:'100%', padding:'0.9rem 1rem', background: loading ? '#d1d5db' : 'linear-gradient(90deg,#78350f,#b45309)', color:'#fef3c7', border:'none', borderRadius:'12px', fontWeight:800, fontSize:'1rem', cursor: loading ? 'not-allowed' : 'pointer', boxShadow:'0 6px 24px rgba(120,53,15,.4)' }}>
                {loading
                  ? 'Please wait…'
                  : isDashboardUpgrade
                    ? `Pay ${UPGRADE_FRAMING} & get premium PDF*`
                    : `Order premium PDF — ${PRICE_FULL_REPORT}`}
              </button>
              {isDashboardUpgrade && (
                <p style={{ textAlign:'center', fontSize:'0.72rem', color:'#78716c', margin:'0.45rem 0 0', lineHeight:1.4 }}>
                  *{COPY_CHECKOUT_NOTE} — PDF emailed within 24h · instant Q&amp;A = Shastra AI {PRICE_KUNDLI_QUESTION} on dashboard
                </p>
              )}

              <p style={{ textAlign:'center', fontSize:'0.75rem', color:'#9ca3af', marginTop:'0.6rem', marginBottom:0 }}>
                🔒 Secure payment via Razorpay · 100% encrypted
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
